/* eslint-disable max-len */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { scroller, animateScroll as scroll } from 'react-scroll';

import { performQuery } from 'lib/api';
import { useOutsideClick } from 'utils/hooks';
import trackAction from 'utils/tracking';
import { ACTION_NODE_BUTTON } from 'constants/Action';

import Cta from 'components/Core/Cta';
import IconSvg from 'components/Core/IconSvg';

const renderChoices = ({
  filter: { id, name: label, slug },
  filterSetSlug,
  onClickAnswerCB,
  questionIndex,
  questionContent,
  isChosen,
}) => {
  const borderClass = isChosen ? 'border-opacity-1' : 'border-opacity-0';
  return (
    <Cta
      styleType="secondary"
      showIcon={false}
      key={`wizard-answer-${id}`}
      className={`w-full mb-4 ${borderClass}`}
      onClick={() => onClickAnswerCB(id, slug, filterSetSlug, questionIndex, label, questionContent)}
    >
      {label}
    </Cta>
  );
};

const Wizard = ({
  wizardQuestions,
  wizardAssistant,
  initialResultCount,
  currentPageId,
  extraClassNames,
  onClickCloseCB,
  conClickFilterCB,
  selectedTheme: { themeName, themeID },
}) => {
  const [wizardState, setWizardState] = useState({
    resultCount: initialResultCount,
    currentQuestion: 0,
    givenFilters: [],
    givenAnswers: [],
    givenAnswersContent: [],
  });
  const ref = useRef();

  const {
    name: assistantName,
    helpText,
    avatar: {
      sm: { url: avatarSm },
      lg: { url: avatarLg },
    },
  } = wizardAssistant;

  const themeQuestions = wizardQuestions.filter((item) => item.theme.name === themeName);

  const { currentQuestion, resultCount, givenAnswers, givenFilters, givenAnswersContent } = wizardState;

  useOutsideClick(ref, () => {
    onClickCloseCB();
  });

  const onClickAnswerCB = async (
    answerID,
    answerSlug,
    filterSetSlug,
    questionIndex,
    answerContent,
    questionContent,
  ) => {
    // Track GTM
    const trackWizardAnswerClick = (question, answer) => trackAction('activity-wizard', answer, question);
    trackWizardAnswerClick(questionContent, answerContent);
    // Process state update.
    const answersList = [...givenAnswers];
    const answersContentList = [...givenAnswersContent];
    const filterList = [...givenFilters];
    let updatedResultCount = resultCount;
    filterList.splice(questionIndex, 1, { filterSet: filterSetSlug, filters: [answerSlug] });
    answersList.splice(questionIndex, 1, parseInt(answerID, 10));
    answersContentList.splice(questionIndex, 1, answerContent);
    const updatedResultCountResp = await performQuery(
      `query($homePageID: Int!, $filters:[FilterType], $themeId: Int) {
            activitiesResultCount(filters: $filters, homePageId: $homePageID, themeId: $themeId)
        }`,
      {
        homePageID: currentPageId,
        filters: filterList,
        themeId: themeID,
      },
    );
    const { activitiesResultCount } = updatedResultCountResp;
    updatedResultCount = activitiesResultCount;

    setWizardState((state) => {
      return {
        ...state,
        currentQuestion: questionIndex >= state.currentQuestion ? questionIndex + 1 : state.currentQuestion,
        givenAnswers: answersList,
        givenFilters: filterList,
        givenAnswersContent: answersContentList,
        resultCount: updatedResultCount,
      };
    });
    scroller.scrollTo(`question-${questionIndex + 1}`, {
      duration: 1500,
      delay: 100,
      smooth: 'easeInOutQuint',
      containerId: 'overlayContainer',
    });

    if (themeQuestions.length - 1 === questionIndex) {
      scroll.scrollToBottom({ containerId: 'overlayContainer' });
    }
  };

  const keyUpAction = (keyevent) => {
    if (keyevent.key === 'Enter') {
      onClickCloseCB();
    }
  };
  return (
    <div
      id="overlayContainer"
      className={`pb-20 lg:pb-0 bg-activity-sport-400 h-full w-full lg:w-1/2 xxl:w-5/12 lg:ml-auto ${extraClassNames}`}
      ref={ref}
    >
      <div className="lg:bg-secondary p-8 pb-0 lg:pb-8 lg:px-20 relative">
        <div
          role="button"
          className="lg:hidden mb-4 hover:text-basic-600 cursor-pointer focus:outline-none w-full flex items-center"
          onClick={() => onClickCloseCB()}
          onKeyUp={(e) => keyUpAction(e)}
          tabIndex="0"
        >
          <IconSvg icon="arrow-left" size={11} className=" text-primary mr-2" />
          <span className="font-bold transition-color ease-out duration-150 ">terug</span>
        </div>

        <div className="flex items-center border-b-2 lg:border-none pb-4 mb-4 lg:mb-0 lg:pb-0">
          <div className="w-60px lg:w-24">
            <img className="rounded-full lg:hidden h-60px w-full" src={avatarSm} alt={assistantName} />
            <img className="rounded-full hidden lg:block lg:h-24 w-full" src={avatarLg} alt={assistantName} />
          </div>
          <div className="text-basic-700 lg:text-basic-100 pl-4 flex-1">{helpText}</div>
          <button
            type="button"
            aria-label="close"
            className="hidden lg:block px-2 absolute right-0 top-0 mt-3 mr-2 cursor-pointer focus:outline-none"
            onClick={() => onClickCloseCB()}
          >
            <IconSvg icon="close" size={25} className="text-primary hover:text-primary-600" />
          </button>
        </div>
      </div>

      {themeQuestions.map((question, index) => {
        const { content: QuestionText, id: qId, filterSet } = question;
        const { filters, slug: filterSetSlug } = filterSet;
        let choosenAnswerContent = '...';
        const haveAnswer = givenAnswers[index];
        let chosenAnswer = null;
        if (haveAnswer) {
          chosenAnswer = filters.find((a) => parseInt(a.id, 10) === givenAnswers[index]);
          choosenAnswerContent = chosenAnswer && chosenAnswer.name;
        }
        const chosenID = haveAnswer && chosenAnswer && chosenAnswer.id;
        return index <= currentQuestion ? (
          <div id={`question-${index}`} key={`wizard-question-${qId}`}>
            {/* Question block */}
            <div className="p-8 py-0 lg:py-8 xl:px-20 lg:max-w-10/12 xl:max-w-11/12">
              <div className="mb-2">
                <span className="h-2 w-2 bg-notification-success rounded-full inline-block mr-2" />
                {assistantName}
              </div>
              <div className="bg-secondary-300 p-6 rounded-base">
                <h3 className="mb-6">{QuestionText}</h3>
                {filters.map((filter) => {
                  const isChosen = chosenID === filter.id;
                  return renderChoices({
                    filter,
                    onClickAnswerCB,
                    filterSetSlug,
                    questionIndex: index,
                    questionContent: QuestionText,
                    isChosen,
                  });
                })}
              </div>
            </div>
            {/* Answer block */}
            <div className="p-8 pt-2 lg:pt-0 xl:px-20 md:max-w-1/2 lg:max-w-2/3 ml-auto  flex flex-col  alight-left">
              <div className="mb-2 text-right">
                Jij
                <span className="h-2 w-2 bg-secondary-600 rounded-full inline-block ml-2" />
              </div>
              {haveAnswer && (
                <div className="bg-white px-6 py-3 lg:px-6 lg:py-4 rounded-lg text-basic-700 text-center font-bold self-end inline-block">
                  {choosenAnswerContent}
                </div>
              )}
              {!haveAnswer && (
                <div className="bg-white px-4 py-3 lg:px-6 lg:py-4 rounded-lg text-basic-700 text-center font-bold self-end inline-block">
                  {choosenAnswerContent}
                </div>
              )}
            </div>
          </div>
        ) : null;
      })}

      <div className="p-8 xl:px-20 fixed bottom-0 left-0 w-full lg:relative lg:bottom-unset lg:left-unset">
        <Cta
          node={ACTION_NODE_BUTTON}
          className="w-full"
          showIcon={false}
          onClick={() => {
            trackAction('activity-wizard', `${(givenAnswersContent || []).join(', ')}`, 'submit');
            conClickFilterCB(givenFilters, themeID, onClickCloseCB);
          }}
        >
          <>
            <span className="rounded-full bg-white text-primary w-8 h-8 flex items-center justify-center mr-4">
              {resultCount}
            </span>
            Activiteiten gevonden
          </>
        </Cta>
      </div>
    </div>
  );
};

Wizard.propTypes = {
  extraClassNames: PropTypes.string,
  onClickCloseCB: PropTypes.func,
  conClickFilterCB: PropTypes.func,

  wizardQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      theme: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }).isRequired,
      content: PropTypes.string,
      filterSet: PropTypes.shape({
        slug: PropTypes.string,
        id: PropTypes.string,
        filters: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            slug: PropTypes.string,
          }),
        ),
      }),
    }),
  ),
  wizardAssistant: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    helpText: PropTypes.string,
    avatar: PropTypes.shape({
      sm: PropTypes.shape({
        url: PropTypes.string,
      }),
      lg: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    content: PropTypes.string,
  }).isRequired,
  selectedTheme: PropTypes.shape({
    themeName: PropTypes.string,
    themeID: PropTypes.number,
  }),
  initialResultCount: PropTypes.number,
  currentPageId: PropTypes.number,
  overviewPage: PropTypes.shape({
    url: PropTypes.string,
  }),
};

Wizard.defaultProps = {
  initialResultCount: 0,
  extraClassNames: '',
  onClickCloseCB: () => {},
  conClickFilterCB: () => {},
  wizardQuestions: [],
  selectedTheme: {
    themeName: 'Sporten',
    themeID: '1',
  },
  currentPageId: null,
  overviewPage: {
    url: null,
  },
};

export default Wizard;
